import { useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { DashboardContainerProps } from "@/components/Dashboard/UI/Cards/Interfaces/DashboardContainer";
import { EmptyContainer } from "@/components/Dashboard/UI/Containers/EmptyContainer";
import { SectionErrorFallback } from "@/components/Dashboard/UI/Section/Components/SectionErrorFallback";
import { SECTION_REGISTRY } from "@/components/Dashboard/Utils/DashboardSectionRegistry";
import {
	ResizableHandle,
	ResizablePanel,
	ResizablePanelGroup,
} from "@/components/ui/resizable";

/**
 * `useDashboardSectionManager` is a custom hook that manages the sections displayed on the dashboard.
 * It determines which sections to render based on user preferences and provides utilities for
 * managing and rendering these sections.
 *
 * @param preferences - An array of strings representing the desired sections and their order.
 *                      These strings must correspond to keys in `SECTION_REGISTRY`.
 * @param initialSections - An array of strings representing the initial sections to be displayed.
 *                          These strings must correspond to keys in `SECTION_REGISTRY`.
 *
 * @returns An object containing:
 *  - `sections`: The current array of section names to be displayed.
 *  - `setSections`: A function to update the `sections` state.
 *  - `renderSections`: A function that takes `DashboardContainerProps` and returns the rendered
 *    React components for the sections. It filters out any invalid section names from `preferences`.
 *  - `getAvailableSections`: A function that returns an array of all possible section names
 *    defined in `SECTION_REGISTRY`.
 *  - `isValidSection`: A type guard function that checks if a given string is a valid section key.
 *
 * The `SECTION_REGISTRY` is a central configuration object where each key is a section name
 * and the value is an object containing:
 *  - `component`: The React component to render for the section.
 *  - `getProps`: A function that extracts the necessary props for the section component from the
 *    main `DashboardContainerProps`.
 *
 * To add a new section to the dashboard, you need to:
 * 1. Create the section component.
 * 2. Add a new entry to `SECTION_REGISTRY` with the section name, component, and `getProps` function.
 */
export function useDashboardSectionManager(
	preferences: string[],
	initialSections: string[] = [],
) {
	const [sections, setSections] = useState<string[]>(preferences);

	const renderSections = (containerProps: DashboardContainerProps) => {
		const validSections = sections.filter(
			(section) => section in SECTION_REGISTRY,
		);

		// Calculate total weight from all sections
		const totalWeight = validSections.reduce((sum, section) => {
			const config = SECTION_REGISTRY[section as keyof typeof SECTION_REGISTRY];
			return sum + config.defaultSize;
		}, 0);

		return validSections.flatMap((section, index) => {
			const sectionConfig =
				SECTION_REGISTRY[section as keyof typeof SECTION_REGISTRY];
			const SectionComponent = sectionConfig.component as React.ComponentType<
				Record<string, unknown>
			>;
			const sectionProps = sectionConfig.getProps(containerProps);

			// Calculate normalized size so all panels add up to exactly 100
			const normalizedSize = (sectionConfig.defaultSize / totalWeight) * 100;

			const elements = [
				<ResizablePanel
					key={section}
					defaultSize={normalizedSize}
					minSize={10}
					maxSize={80}
					order={index}
				>
					<ErrorBoundary
						fallback={<SectionErrorFallback title={sectionConfig.label} />}
					>
						<SectionComponent {...sectionProps} />
					</ErrorBoundary>
				</ResizablePanel>,
			];

			// This adds a handle between each section
			if (index < validSections.length - 1) {
				elements.push(<ResizableHandle key={`handle-${section}`} withHandle />);
			}

			return elements;
		});
	};

	const renderFullContainer = (containerProps: DashboardContainerProps) => {
		if (sections.length === 0) {
			return <EmptyContainer />;
		}

		return (
			<ResizablePanelGroup
				direction="horizontal"
				className="!h-auto min-h-[85vh] !w-full rounded-lg border-2"
				autoSaveId="dashboard-sections-layout"
			>
				{renderSections(containerProps)}
			</ResizablePanelGroup>
		);
	};

	const getAvailableSections = () => {
		return Object.keys(SECTION_REGISTRY);
	};

	const isValidSection = (
		section: string,
	): section is keyof typeof SECTION_REGISTRY => {
		return Object.keys(SECTION_REGISTRY).includes(section);
	};

	useEffect(() => {
		// Filter the initial sections to only include valid and user preferences
		setSections(
			initialSections.filter(
				(section) => isValidSection(section) && preferences.includes(section),
			),
		);
	}, [initialSections, setSections, preferences]);

	return {
		sections,
		setSections,
		getAvailableSections,
		isValidSection,
		renderFullContainer,
	};
}

